import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { renameKeysFromSDKRequestToRESTRequest, renameKeysFromRESTResponseToSDKResponse, } from '@wix/sdk-runtime/rename-all-nested-keys';
import * as ambassadorWixDataV2Index from './data-v2-index-indexes.http';
/**
 * Order determines how values are ordered in the index. This is important when
 * ordering and/or range querying by indexed fields.
 */
export var Order;
(function (Order) {
    Order["ASC"] = "ASC";
    Order["DESC"] = "DESC";
})(Order || (Order = {}));
export var Status;
(function (Status) {
    /** Place holder. Never returned by the service. */
    Status["UNKNOWN"] = "UNKNOWN";
    /** Index creation is in progress. */
    Status["BUILDING"] = "BUILDING";
    /** Index has been successfully created and can be used in queries. */
    Status["ACTIVE"] = "ACTIVE";
    /** Index is in the process of being dropped. */
    Status["DROPPING"] = "DROPPING";
    /** Index has been dropped successfully. */
    Status["DROPPED"] = "DROPPED";
    /** Index creation has failed. */
    Status["FAILED"] = "FAILED";
    /** Index contains incorrectly indexed data. */
    Status["INVALID"] = "INVALID";
})(Status || (Status = {}));
export var Environment;
(function (Environment) {
    Environment["LIVE"] = "LIVE";
    Environment["SANDBOX"] = "SANDBOX";
    Environment["SANDBOX_PREFERRED"] = "SANDBOX_PREFERRED";
})(Environment || (Environment = {}));
/**
 * Creates an index for a data collection.
 *
 * The index can't be used immediately, as the process of generating the index takes time.
 * You can check whether your index is ready using `listIndexes()`.
 *
 * Note that when an index fails to create, the failed index still occupies a slot.
 * To remove the failed index and free up the slot for a new index, use `dropIndex()`.
 * @param dataCollectionId - ID of the data collection for which to generate the index.
 * @param index - Details of the index to be created.
 * @public
 * @requiredField dataCollectionId
 * @requiredField index
 * @requiredField index.fields
 * @requiredField index.fields.path
 * @requiredField index.name
 * @param options - Options for creating an index.
 * @permissionId WIX_DATA.CREATE_INDEX
 * @permissionScope Managa Data Indexes
 * @permissionScopeId SCOPE.DC-DATA.INDEXES-MANAGE
 * @applicableIdentity APP
 * @returns Details of the index being generated.
 */
export async function createIndex(dataCollectionId, index) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[2];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        dataCollectionId: dataCollectionId,
        index: index,
    });
    const reqOpts = ambassadorWixDataV2Index.createIndex(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return renameKeysFromRESTResponseToSDKResponse(result.data)?.index;
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: {},
            explicitPathsToArguments: { dataCollectionId: '$[0]', index: '$[1]' },
            singleArgumentUnchanged: false,
        }, ['dataCollectionId', 'index']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
/**
 * Removes an index from a data collection.
 *
 * The process of dropping an index from a collection takes time.
 * You can check whether your index has been dropped by calling `listIndexes()`.
 * @param dataCollectionId - ID of the data collection for which the index to be dropped is defined.
 * @param indexName - Name of the index to drop.
 * @public
 * @requiredField dataCollectionId
 * @requiredField indexName
 * @param options - Options for dropping an index.
 * @permissionId WIX_DATA.DROP_INDEX
 * @permissionScope Managa Data Indexes
 * @permissionScopeId SCOPE.DC-DATA.INDEXES-MANAGE
 * @applicableIdentity APP
 */
export async function dropIndex(dataCollectionId, indexName) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[2];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        dataCollectionId: dataCollectionId,
        indexName: indexName,
    });
    const reqOpts = ambassadorWixDataV2Index.dropIndex(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: {},
            explicitPathsToArguments: {
                dataCollectionId: '$[0]',
                indexName: '$[1]',
            },
            singleArgumentUnchanged: false,
        }, ['dataCollectionId', 'indexName']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
/**
 * Lists all indexes defined for a data collection.
 *
 * When an index's status is `ACTIVE`, it is ready to use.
 * While it is still being created, its status is `BUILDING`.
 *
 * When an index's status is `DROPPED`, it has been dropped successfully.
 * While it is still in the process of being removed, its status is `DROPPING`.
 * @param dataCollectionId - ID of the data collection for which to list indexes.
 * @public
 * @requiredField dataCollectionId
 * @param options - Options for retrieving a list of indexes.
 * @permissionId WIX_DATA.LIST_INDEXES
 * @permissionScope Managa Data Indexes
 * @permissionScopeId SCOPE.DC-DATA.INDEXES-MANAGE
 * @applicableIdentity APP
 */
export async function listIndexes(dataCollectionId, options) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[2];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        dataCollectionId: dataCollectionId,
        paging: options?.paging,
    });
    const reqOpts = ambassadorWixDataV2Index.listIndexes(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return renameKeysFromRESTResponseToSDKResponse(result.data);
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: {},
            explicitPathsToArguments: {
                dataCollectionId: '$[0]',
                paging: '$[1].paging',
            },
            singleArgumentUnchanged: false,
        }, ['dataCollectionId', 'options']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
//# sourceMappingURL=data-v2-index-indexes.universal.js.map